import { getPlans, getSubscription } from '../api/endpoints.js';
import { useApi } from '../api/hooks.js';
import { paidSubscription } from '../flow/entitlement.js';
import { APP_STORE_URL, PLAY_STORE_URL } from '../flow/stores.js';
import { Async, Badge, KeyValue, Note, Panel, Raw, Screen } from '../ui/kit.js';

/**
 * Plans and billing — a READ-ONLY surface since 2026-09-07 (spec §10).
 *
 * There is nothing to drive from a browser: Play Billing and StoreKit are
 * in-app only, so a purchase cannot start here, and neither store lets a
 * server change a plan or cancel one — those happen on the store's own
 * subscription page, which the api hands us as `manage_url`.
 *
 * What this screen is FOR is reading the two payloads the mobile clients get,
 * so a discrepancy shows up here first: which replacement mode an upgrade will
 * use (`store_policy`), which moves the server considers sensible
 * (`changeable_to`), and where a cancellation goes.
 */
export function PlansScreen() {
  const plans = useApi(getPlans, []);
  const subscription = useApi(getSubscription, []);

  const live = subscription.data?.subscription ?? null;
  const paying = paidSubscription(live);
  const changeableTo = subscription.data?.changeable_to ?? [];

  return (
    <Screen
      title="Plans & billing"
      lede="Payment is in the apps: Play Billing and StoreKit are in-app only, so there is no checkout here and no plan-change or cancel endpoint to call. This screen reads GET /v1/plans and GET /v1/billing/subscription — the same payloads the mobile clients get."
    >
      <Panel title="current subscription">
        <Async state={subscription}>
          {(data) => (
            <>
              {data.subscription === null ? (
                <p className="empty">no subscription — 200 with null, not a 404</p>
              ) : (
                <KeyValue
                  rows={[
                    ['plan_code', <code key="p">{data.subscription.plan_code}</code>],
                    // `paid` is the one fact payment changes: since 0023 every
                    // account holds a live FREE row from signup.
                    ['paid', String(data.subscription.paid)],
                    ['provider', data.subscription.provider],
                    ['status', <Badge key="s">{data.subscription.status}</Badge>],
                    ['trial_ends_at', data.subscription.trial_ends_at],
                    ['current_period_start', data.subscription.current_period_start],
                    ['current_period_end', data.subscription.current_period_end],
                    ['cancel_at', data.subscription.cancel_at],
                  ]}
                />
              )}
              <KeyValue
                rows={[
                  ['seats_used', data.seats_used],
                  ['seat_limit', data.seat_limit],
                  ['trial', data.trial === null ? null : JSON.stringify(data.trial)],
                  ['manage_url', data.manage_url],
                ]}
              />
              {data.manage_url !== null && (
                <p className="muted">
                  Cancelling happens at the store — neither Apple nor Google lets a server do it,
                  which is why there is no DELETE here any more.{' '}
                  <a href={data.manage_url} target="_blank" rel="noreferrer noopener">
                    manage this subscription →
                  </a>
                </p>
              )}
              <Raw label="GET /v1/billing/subscription" value={data} />
            </>
          )}
        </Async>
      </Panel>

      <Panel title="plans">
        <Async state={plans}>
          {(data) => (
            <>
              <div className="scroll-x">
                <table>
                  <thead>
                    <tr>
                      <th>plan_code</th>
                      <th>name</th>
                      <th>tier</th>
                      <th>seats</th>
                      <th>interval</th>
                      <th>price_usd</th>
                      <th>apple_product_id</th>
                      <th>google_product_id</th>
                      <th>base_plan</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.plans.map((plan) => (
                      <tr key={plan.plan_code}>
                        <td>
                          <code>{plan.plan_code}</code>
                        </td>
                        <td>{plan.display_name}</td>
                        <td>{plan.tier}</td>
                        <td>{plan.seat_limit}</td>
                        <td>{plan.billing_interval}</td>
                        <td>{plan.price_usd ?? '—'}</td>
                        <td>
                          <code>{plan.apple_product_id ?? '—'}</code>
                        </td>
                        <td>
                          <code>{plan.google_product_id ?? '—'}</code>
                        </td>
                        <td>{plan.google_base_plan_id ?? '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {data.plans.length === 0 && <p className="empty">no plans seeded</p>}

              <Note>
                <strong>store_policy</strong> — an upgrade uses{' '}
                <code>{data.store_policy.google.upgrade_replacement_mode}</code> and a downgrade{' '}
                <code>{data.store_policy.google.downgrade_replacement_mode}</code> on Play. The
                first is the owner&rsquo;s rule: the old plan ends and the new one starts today at
                full price, with no credit for the unused remainder. There is no Apple equivalent —
                StoreKit takes no replacement mode, and Apple derives the behaviour from how the
                products are ranked in the App Store Connect subscription group.
              </Note>
              <Raw label="GET /v1/plans" value={data} />
            </>
          )}
        </Async>

        <p className="muted" style={{ marginTop: 10 }}>
          {paying === null
            ? `No PAID subscription${live === null ? '' : ` (on ${live.plan_code}, paid: false)`}. A plan is bought in the app — there is no checkout on the web.`
            : changeableTo.length === 0
              ? `Paying for ${paying.plan_code} (${paying.status}) — and no move is on offer. An annual household plan runs to its end date: cancel at the store to stop it renewing (coverage holds until then, no refund).`
              : `Paying for ${paying.plan_code} (${paying.status}). changeable_to: ${changeableTo.map((move) => `${move.plan_code} (${move.direction}, ${move.google_replacement_mode})`).join(', ')} — the moves the server considers sensible, each with the direction and the Play replacement mode the app must pass. The app offers these; the purchase itself happens at the store.`}
        </p>
      </Panel>

      <Panel title="buying a plan">
        <Note>
          A purchase cannot start from a browser. The app makes it against the store and sends the
          resulting handle — an Apple transaction id, or a Play purchase token — to{' '}
          <code>POST /v1/billing/purchase</code>, which derives the plan from the store&rsquo;s
          verified answer rather than from anything the client claims.
        </Note>
        <p className="muted">
          <a href={APP_STORE_URL} target="_blank" rel="noreferrer noopener">
            App Store →
          </a>{' '}
          ·{' '}
          <a href={PLAY_STORE_URL} target="_blank" rel="noreferrer noopener">
            Google Play →
          </a>
        </p>
      </Panel>
    </Screen>
  );
}
